import React, { useState, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import './ReceiptGallery.css';
import { getDocument } from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import Receipt from 'src/components/project-billing/models/Receipt';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const imageList: string[] = [];

const formatCurrency = (value: number) =>
  (value ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const formatDateOrBlank = (value: string | undefined): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

// Shared by both the network-fetch loading branch and Document's own (local-parse) loading
// branch below - one spinner look regardless of which wait it's covering.
const LOADING_PDF_TEXT = (
  <div className="loading-text" style={{ textAlign: 'center', height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
    <span>L</span><span>o</span><span>a</span><span>d</span><span>i</span><span>n</span><span>g</span>
    <span>&nbsp;&nbsp;</span>
    <span>P</span><span>D</span><span>F</span>
  </div>
);

const isPDF = (filename: string): boolean => {
  let isAPdfFile = false;
  if (!!filename && typeof filename === 'string') {
    isAPdfFile = filename.toLowerCase().endsWith('.pdf');
  } else {
    console.log(filename);
  }

  return isAPdfFile;
};

interface IReceiptGalleryProps {
    pdfBytes?: Uint8Array | ArrayBuffer;
    imageSrc?: string;
    receiptThumbnails?: Array<Receipt>;
    setSelectedThumbnail?: (selectedThumbnail: number) => void;
    // True while the parent's fetch for the selected receipt's full-size bytes is in flight. The
    // Document below has its own "loading" placeholder, but that only covers react-pdf's local
    // parse of bytes already in memory (near-instant) - the real wait is the network fetch before
    // pdfData exists at all, during which neither branch below would otherwise show anything.
    loading?: boolean;
}

const ReceiptGallery = (props: IReceiptGalleryProps) => {
  const [scale, setScale] = useState(1);
  const [page, setPage] = useState<number>(1);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [darkMode, setDarkMode] = useState<boolean>(false);
  const [mainImageVisible, setMainImageVisible] = useState<boolean>(true);
  const [pdfData, setPdfData] = useState<Uint8Array>();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const thumbnailContainerRef = useRef<HTMLDivElement | null>(null);
  const thumbnailsRef = useRef<(HTMLImageElement | null)[]>([]);
  const [numPages, setNumPages] = useState<Record<string, number>>({});
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [receiptThumbnails, setReceiptThumbnails] = useState<Array<string>>(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);

  const zoomIn = () => setScale((prev) => prev + 0.25);
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.25));
  const nextPage = () => {
    if (!pdfData) {
      const currentFile =
        receiptThumbnails?.length
          ? receiptThumbnails[currentIndex]
          : imageList[currentIndex];
      const currentNumPages = numPages[currentFile] ?? 1; // fallback to 1 if not loaded

      setPage(prev => (prev + 1 < currentNumPages ? prev + 1 : prev));
    } else {
      setPage(prev => Math.min(prev + 1, pdfPageCount));
    }
  };

  const getNumPdfPages = async () => {
    if (!pdfData) {
        return 0;
    }

    const pdf = await getDocument({ data: pdfData }).promise;
    return pdf.numPages;
  };

  const getNumPages = () => {
    const currentFile =
      receiptThumbnails?.length
        ? receiptThumbnails[currentIndex]
        : imageList[currentIndex];
    return numPages[currentFile] ?? 1;
  };
    
  const onLoadSuccess = (fileName: string) => (pdf: PDFDocumentProxy) => {
    setNumPages(prev => ({ ...prev, [fileName]: pdf.numPages }));
    setPage(1);
  };

  const playPaperSound = (): void => {
    const audio = audioRef.current;
    if (audio) {
      audio.currentTime = 0;
      audio.play().catch(() => { console.log('Error playing paper shuffle.'); });
    }
  };

  useEffect(() => {
    thumbnailsRef.current = [];
    if (props?.setSelectedThumbnail) {
      props.setSelectedThumbnail(0);
    }
  }, []);

  useEffect(() => {
      const loadPageCount = async () => {
          if (pdfData) {
              const count = await getNumPdfPages();
              setPdfPageCount(count);
          } else {
              setPdfPageCount(0);
          }
      };

      loadPageCount();
  }, [pdfData]);

  useEffect(() => {
    if (props?.imageSrc) {
      if (typeof props.imageSrc === 'string') {
        setImageSrc(props.imageSrc);
      }
    }
  }, [props?.imageSrc]);
  
  useEffect(() => {
    if (props?.receiptThumbnails) {
      let processedReceiptThumbnails = new Array<string>();
      props?.receiptThumbnails.forEach((rt: Receipt) => {
        if (rt.base64ReceiptString || rt.base64InvoiceThumbnailString) {
        const base64Prefix = 'data:application/pdf;base64,';
        const base64PdfString = rt?.base64ReceiptString ? rt.base64ReceiptString : rt?.base64InvoiceThumbnailString ?
        rt.base64InvoiceThumbnailString : null;
        const pdfStringData = base64PdfString.startsWith(base64Prefix)
          ? base64PdfString
          : base64Prefix + base64PdfString;
        processedReceiptThumbnails.push(pdfStringData);
        }
      });
      if (processedReceiptThumbnails.length) {
        setReceiptThumbnails(processedReceiptThumbnails);
      }
    }
  }, [props?.receiptThumbnails]);

  useEffect(() => {
    if (props?.imageSrc && typeof props?.imageSrc === 'string' && props?.imageSrc?.length > 0) {
      const base64Prefix = 'data:application/pdf;base64,';
      const pdfStringData = props?.imageSrc.startsWith(base64Prefix)
        ? props?.imageSrc
        : base64Prefix + props?.imageSrc;
      imageList.push(pdfStringData);
    }
  }, [imageSrc]);

  useEffect(() => {
    if (props?.pdfBytes) {
      if (props.pdfBytes instanceof ArrayBuffer) {
        setPdfData(new Uint8Array(props.pdfBytes));
      } else if (props.pdfBytes instanceof Uint8Array) {
        setPdfData(props.pdfBytes);
      }
    }
  }, [props?.pdfBytes]);

  const handleThumbnailClick = (index: number): void => {
    playPaperSound();
    setMainImageVisible(false);
    setTimeout(() => {
      setCurrentIndex(index);
      setPage(1);
      setMainImageVisible(true);
    }, 50);
    scrollToThumbnail(index);
    props.setSelectedThumbnail(index);
  };

  const scrollToThumbnail = (index: number): void => {
    const container = thumbnailContainerRef.current;
    const thumb = thumbnailsRef.current[index];
    if (!thumb || !container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();

    if (thumbRect.left < containerRect.left || thumbRect.right > containerRect.right) {
      container.scrollLeft += thumbRect.left - containerRect.left - 10;
    }
  };

  const cycleImage = (direction: number): void => {
    playPaperSound();
    let nextIndex = currentIndex + direction;
    if (nextIndex < 0) {
      nextIndex = Array.isArray(receiptThumbnails) ? receiptThumbnails.length - 1 : imageList.length - 1;
    }
    if (nextIndex >= (Array.isArray(receiptThumbnails) ? receiptThumbnails.length : imageList.length)) {
      nextIndex = 0;
    }
    handleThumbnailClick(nextIndex);
  };

  return (
    <div style={{background: 'transparent'}} >
        <div
          style={{
            position: 'absolute',
            top: '1px',
            right: '100px',
            display: 'block',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <button style={{whiteSpace: 'nowrap'}} className="dark-mode-toggle" onClick={() => setDarkMode(!darkMode)}>
            🌓 Toggle Mode
          </button>
        </div>
        <div
          style={{
            position: 'absolute',
            top: '40px',
            right: '100px',
            display: 'block',
          }}
        >
          <button style={{whiteSpace: 'nowrap'}} className="dark-mode-toggle" onClick={zoomOut}>
            ➖ Zoom Out
          </button>
        </div>
        <div
          style={{
            position: 'absolute',
            top: '80px',
            right: '100px',
            display: 'block',
          }}
        >
          <button style={{whiteSpace: 'nowrap'}} className="dark-mode-toggle" onClick={zoomIn}>
            ➕ Zoom In
          </button>
        </div>
        <div
          style={{
            position: 'absolute',
            top: '120px',
            right: '100px',
            display: 'block',
          }}
        >
          <button style={{whiteSpace: 'nowrap'}} className="dark-mode-toggle" onClick={nextPage}>
            {`➕ Next Page (${page} of ${pdfData ? pdfPageCount : getNumPages()})`}
          </button>
        </div>
        <div
          className={`gallery-container ${darkMode ? 'dark-mode' : ''}`}
          style={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden',
              boxSizing: 'border-box'
          }}
        >
        {props.loading ? (
          // Fetch for the selected receipt's full-size bytes is still in flight - this is the
          // slow part (network), unlike Document's own "loading" below which only covers parsing
          // bytes already in memory, so it needs its own branch rather than waiting on pdfData.
          <div id="main-image" className={mainImageVisible ? 'visible' : ''}>
            {LOADING_PDF_TEXT}
          </div>
        ) : (!pdfData && !isPDF(receiptThumbnails?.length ? receiptThumbnails[currentIndex] : imageList[currentIndex])) ? (
          // Image
          <img
            id="main-image"
            src={receiptThumbnails?.length ? receiptThumbnails[currentIndex] : imageList[currentIndex]}
            alt="Main Receipt"
            className={mainImageVisible ? 'visible' : ''}
          />
        ) : (
          // PDF
          <div
            id="main-image"
            className={mainImageVisible ? 'visible' : ''}
            style={{
              overflowY: pdfData ? 'auto' : 'hidden', // hide scrollbar if PDF not loaded
              height: pdfData ? '70vh' : 'auto',      // only constrain height when PDF loaded
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Document
              file={{ data: pdfData }}
              loading={LOADING_PDF_TEXT}
            >
              <div style={{ display: 'inline-block' }}>
                <Page
                  pageNumber={page}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </div>
            </Document>
          </div>
        )}
        <div className={'thumbnail-row'}>
          <div className={'arrow'} onClick={() => cycleImage(-1)}>&#10094;</div>
          <div className={'thumbnails'} ref={thumbnailContainerRef}>
            {(!!receiptThumbnails && receiptThumbnails?.length > 0 ? receiptThumbnails : imageList).map((filename, index) => (
              <div className={'thumbnail'} key={filename + index}>
                {isPDF(filename) ? (
                  <div className={`pdf-wrapper ${currentIndex === index ? 'active' : ''}`} onClick={() => handleThumbnailClick(index)} style={{ cursor: 'pointer' }}>
                  <Document
                    file={filename}
                    onLoadSuccess={onLoadSuccess(filename)}
                    loading={<div>Loading...</div>}
                  >
                    <Page
                      pageNumber={1}
                      width={100} // small enough to fit like an image thumbnail
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                  <label style={{ fontSize: '0.7rem', marginBottom: '4px' }}>
                    {numPages[filename] ? `${numPages[filename]} page(s)` : 'Loading...'}
                  </label>
                </div>
              ) : (
                <img
                  src={filename}
                  alt={filename}
                  className={currentIndex === index ? 'active' : ''}
                  onClick={() => handleThumbnailClick(index)}
                  ref={(el) => thumbnailsRef.current[index] = el}
                />)}
                <div className={'caption'}>
                  {!!props?.receiptThumbnails && props?.receiptThumbnails.length > 0 ?
                    [props.receiptThumbnails[index]?.billingCode, props.receiptThumbnails[index]?.description].filter(Boolean).join(' - ') || 'unknown'
                    : 'unknown'}<br />
                  {!!props?.receiptThumbnails && props?.receiptThumbnails.length > 0 ? (formatDateOrBlank(props?.receiptThumbnails[index]?.fileDate) || 'unknown') : 'unknown'}<br />
                  {!!props?.receiptThumbnails && props?.receiptThumbnails.length > 0 ? props?.receiptThumbnails[index]?.fileName : 'unknown'}<br />
                  {!!props?.receiptThumbnails && props?.receiptThumbnails.length > 0 && props.receiptThumbnails[index]?.amount != null
                    ? formatCurrency(props.receiptThumbnails[index].amount)
                    : 'unknown'}
                </div>
              </div>
            ))}
          </div>
          <div className={'arrow'} onClick={() => cycleImage(1)}>&#10095;</div>
        </div>
        <audio ref={audioRef} src={'paper-shuffle.mp3'} preload={'auto'} />
        </div>
    </div>
  );
};

export default ReceiptGallery;


// WEBPACK FOOTER //
// ./src/components/project-billing/pages/ReceiptViewer.tsx